import { cn } from '@gymcoach/ui';
import { NavLink } from 'react-router-dom';

import { ROLE_LABELS } from '@/auth/roles';
import { useMe } from '@/auth/useMe';
import { visibleNavigation } from '@/routes/navigation';

import { GymMark } from './GymMark';
import { NavGlyph } from './icons';
import { useGymLogoUrl } from './useGymLogoUrl';

export interface SidebarProps {
  /** Phone and tablet only. The sidebar is always present from `lg` up. */
  open: boolean;
  onNavigate: () => void;
}

/**
 * The main navigation (ported from legalcrm). Driven by role: a trainer's menu has no
 * Payments, Trainers, Import or Settings (routes/navigation.ts). The gym's name and mark
 * sit at the top, so the CRM feels like the gym's own (UX-PRINCIPLES §3).
 */
export function Sidebar({ open, onNavigate }: SidebarProps) {
  const { profile, gym, role } = useMe();
  const logoUrl = useGymLogoUrl(gym.logo_path);
  const sections = visibleNavigation(role);

  return (
    <>
      {/* Phone scrim. Not focusable: tapping it closes the drawer. */}
      {open && <div className="fixed inset-0 z-30 bg-scrim lg:hidden" onClick={onNavigate} aria-hidden="true" />}

      <nav
        aria-label="Main"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-surface',
          'transition-transform duration-200 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 shrink-0 items-center border-b px-4">
          <GymMark name={gym.name} logoUrl={logoUrl} />
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section) => (
            <div key={section.key} className="mb-5 last:mb-0">
              {section.label && (
                <h2 className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  {section.label}
                </h2>
              )}
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => (
                  <li key={item.key}>
                    <NavLink
                      to={item.to}
                      // `end` on the dashboard only, or "/" matches every route.
                      end={item.to === '/'}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex min-h-[40px] items-center gap-3 rounded-sm px-2.5 py-2 text-base transition-colors',
                          isActive
                            ? 'bg-primary-subtle font-semibold text-primary'
                            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                        )
                      }
                    >
                      <NavGlyph name={item.icon} />
                      <span className="flex-1">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t px-4 py-3">
          <p className="truncate text-sm font-medium text-ink">{profile.full_name}</p>
          <p className="truncate text-xs text-ink-muted">{ROLE_LABELS[role]}</p>
        </div>
      </nav>
    </>
  );
}
